import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import Input from '../ui/Input';

import { productService } from '../../services/productService';
import { adminOrderService } from '../../services/adminOrderService';
import { inventoryService } from '../../services/inventoryService';
import { supabase } from '../../lib/supabase';
import SmartAddProductModal from '../../pages/admin-dashboard/components/SmartAddProductModal';

const StoreManager = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(null);
  const [showSmartAddModal, setShowSmartAddModal] = useState(false);
  const [trackingData, setTrackingData] = useState({ company: '', number: '' });
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [cancelReason, setCancelReason] = useState('');
  const [deleteOrderConfirm, setDeleteOrderConfirm] = useState(null); // order to delete
  const [deleteOrderLoading, setDeleteOrderLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [sendingMysoftId, setSendingMysoftId] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);

  // ── Inline order-edit states ──
  const [isEditingTotal, setIsEditingTotal] = useState(false);
  const [newTotal, setNewTotal] = useState('');
  const [removingItemId, setRemovingItemId] = useState(null);

  // Category management state
  const DEFAULT_CATEGORIES = ['SUP ve Kano', 'Sup Motorları ve Kürek', 'Giyim', 'Aksesuar ve Yedek Parça'];
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [newCategoryInput, setNewCategoryInput] = useState('');

  // Inventory management state
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [inventoryStats, setInventoryStats] = useState(null);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [selectedProductForInventory, setSelectedProductForInventory] = useState(null);
  const [inventoryTransactions, setInventoryTransactions] = useState([]);
  const [adjustmentForm, setAdjustmentForm] = useState({
    quantityChange: '',
    transactionType: 'adjustment',
    notes: ''
  });

  // Product form state
  const [productForm, setProductForm] = useState({
    title: '',
    description: '',
    price: '',
    discountedPrice: '',
    compareAtPrice: '',
    stockQuantity: '',
    lowStockThreshold: '10',
    trackInventory: true,
    isFeatured: false,
    category: '',
    brand: '',
    sku: '',
    weight: '',
    dimensions: { length: '', width: '', height: '', unit: 'cm' },
    imageUrls: [],
    technicalSpecs: {},
    images: [],
    purchasePrice: '',
    vatRate: '20',
    variants: [],
    isBundle: false,
    bundleItems: [],
    bundleDiscountPercentage: '0'
  });

  // Variant management state
  const [newVariant, setNewVariant] = useState({ color: '', size: '', stock: '' });
  const [showVariantCloneDropdown, setShowVariantCloneDropdown] = useState(false);

  // Bundle child product selector state
  const [bundleChildSearch, setBundleChildSearch] = useState('');

  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageAlt, setNewImageAlt] = useState('');
  const [newSpecKey, setNewSpecKey] = useState('');
  const [newSpecValue, setNewSpecValue] = useState('');
  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  // Initialize categories from products + defaults
  useEffect(() => {
    if (products?.length > 0) {
      const productCategories = products
        ?.map(p => p?.category)
        ?.filter(c => c && c?.trim() !== '');
      const merged = Array.from(new Set([...DEFAULT_CATEGORIES, ...productCategories]));
      setCategories(merged);
    }
  }, [products]);

  useEffect(() => {
    // Load inventory stats and alerts
    loadInventoryData();
    
    // Subscribe to real-time alerts
    const subscription = inventoryService?.subscribeToAlerts((payload) => {
      console.log('New inventory alert:', payload);
      loadInventoryData();
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (activeTab === 'products') {
        const productsData = await productService?.getAllAdmin();
        setProducts(productsData);
      } else {
        const ordersData = await adminOrderService?.getAllOrders();
        setOrders(ordersData);
      }
    } catch (err) {
      setError(err?.message || 'Veri yüklenirken hata oluştu');
      console.error('Error loading data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadInventoryData = async () => {
    try {
      const [alerts, stats] = await Promise.all([
        inventoryService?.getLowStockAlerts(),
        inventoryService?.getInventoryStats()
      ]);
      setLowStockAlerts(alerts);
      setInventoryStats(stats);
    } catch (err) {
      console.error('Error loading inventory data:', err);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setProductForm({
      title: '',
      description: '',
      price: '',
      discountedPrice: '',
      compareAtPrice: '',
      stockQuantity: '',
      lowStockThreshold: '10',
      trackInventory: true,
      isFeatured: false,
      category: '',
      brand: '',
      sku: '',
      weight: '',
      dimensions: { length: '', width: '', height: '', unit: 'cm' },
      imageUrls: [],
      technicalSpecs: {},
      images: [],
      purchasePrice: '',
      vatRate: '20',
      variants: [],
      isBundle: false,
      bundleItems: [],
      bundleDiscountPercentage: '0'
    });
    setShowVariantCloneDropdown(false);
    setBundleChildSearch('');
    setShowProductModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    // If compareAtPrice exists and is greater than price, it means there's a campaign price
    // compareAtPrice = original price, price = discounted active price
    const hasActiveCampaign = product?.compareAtPrice && parseFloat(product?.compareAtPrice) > parseFloat(product?.price);
    setProductForm({
      title: product?.title || '',
      description: product?.description || '',
      price: hasActiveCampaign ? String(product?.compareAtPrice) : String(product?.price || ''),
      discountedPrice: hasActiveCampaign ? String(product?.price) : '',
      compareAtPrice: product?.compareAtPrice || '',
      stockQuantity: product?.stockQuantity || '',
      lowStockThreshold: product?.lowStockThreshold || '10',
      trackInventory: product?.trackInventory !== false,
      isFeatured: product?.isFeatured || false,
      category: product?.category || '',
      brand: product?.brand || null,
      sku: product?.sku || null,
      weight: product?.weight || '',
      dimensions: product?.dimensions || { length: '', width: '', height: '', unit: 'cm' },
      imageUrls: product?.imageUrls || [],
      technicalSpecs: product?.technicalSpecs || {},
      images: product?.images || [],
      purchasePrice: product?.purchasePrice ?? product?.purchase_price ?? '',
      vatRate: product?.vatRate ?? product?.vat_rate ?? '20',
      variants: product?.variants || [],
      isBundle: product?.isBundle ?? product?.is_bundle ?? false,
      bundleItems: product?.bundleItems ?? product?.bundle_items ?? [],
      bundleDiscountPercentage: String(product?.bundleDiscountPercentage ?? product?.bundle_discount_percentage ?? '0')
    });
    setShowVariantCloneDropdown(false);
    setBundleChildSearch('');
    setShowProductModal(true);
  };

  const handleSaveProduct = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // If variants exist, compute total stock from variant stocks
      const hasVariants = productForm?.variants && productForm?.variants?.length > 0;
      const computedStock = hasVariants
        ? productForm?.variants?.reduce((sum, v) => sum + (parseInt(v?.stock) || 0), 0)
        : parseInt(productForm?.stockQuantity) || 0;

      // ── PRICING LOGIC ──────────────────────────────────────────────────────
      // For bundles: auto-calculate price from bundle items sum × discount
      // For standard products: if discountedPrice is set, price = discountedPrice, compareAtPrice = original price
      let finalPrice, finalCompareAtPrice;

      if (productForm?.isBundle) {
        const bundleItemsSum = (productForm?.bundleItems || [])?.reduce((sum, bi) => sum + (parseFloat(bi?.price) || 0), 0);
        const discountPct = parseFloat(productForm?.bundleDiscountPercentage) || 0;
        finalPrice = discountPct > 0
          ? parseFloat((bundleItemsSum * (1 - discountPct / 100))?.toFixed(2))
          : bundleItemsSum;
        finalCompareAtPrice = discountPct > 0 ? bundleItemsSum : null;
      } else {
        const basePrice = parseFloat(productForm?.price) || 0;
        const discountedPrice = productForm?.discountedPrice ? parseFloat(productForm?.discountedPrice) : null;
        if (discountedPrice && discountedPrice < basePrice) {
          finalPrice = discountedPrice;
          finalCompareAtPrice = basePrice;
        } else {
          finalPrice = basePrice;
          finalCompareAtPrice = productForm?.compareAtPrice ? parseFloat(productForm?.compareAtPrice) : null;
        }
      }
      // ──────────────────────────────────────────────────────────────────────

      const productData = {
        title: productForm?.title,
        description: productForm?.description,
        price: finalPrice,
        compareAtPrice: finalCompareAtPrice,
        stockQuantity: computedStock,
        lowStockThreshold: parseInt(productForm?.lowStockThreshold) || 10,
        trackInventory: productForm?.trackInventory,
        isFeatured: productForm?.isFeatured || false,
        category: productForm?.category,
        brand: productForm?.brand || null,
        sku: productForm?.sku || null,
        weight: productForm?.weight ? parseFloat(productForm?.weight) : null,
        dimensions: productForm?.dimensions,
        imageUrls: productForm?.imageUrls,
        technicalSpecs: productForm?.technicalSpecs,
        images: productForm?.images,
        purchasePrice: parseFloat(productForm?.purchasePrice) || 0,
        vatRate: parseFloat(productForm?.vatRate) || 20,
        variants: hasVariants ? productForm?.variants : null,
        isBundle: productForm?.isBundle || false,
        bundleItems: productForm?.isBundle ? (productForm?.bundleItems || []) : null,
        bundleDiscountPercentage: parseFloat(productForm?.bundleDiscountPercentage) || 0
      };

      if (editingProduct) {
        await productService?.update(editingProduct?.id, productData);
      } else {
        await productService?.create(productData);
      }

      setShowProductModal(false);
      await loadData();
      await loadInventoryData();
    } catch (err) {
      setError(err?.message || 'Ürün kaydedilirken hata oluştu');
      console.error('Error saving product:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) return;

    setIsLoading(true);
    setError(null);

    try {
      await productService?.delete(productId);
      await loadData();
      await loadInventoryData();
    } catch (err) {
      setError(err?.message || 'Ürün silinirken hata oluştu');
      console.error('Error deleting product:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleFeatured = async (product) => {
    try {
      await productService?.update(product?.id, {
        title: product?.title,
        description: product?.description,
        price: product?.price,
        compareAtPrice: product?.compareAtPrice,
        stockQuantity: product?.stockQuantity,
        lowStockThreshold: product?.lowStockThreshold,
        trackInventory: product?.trackInventory,
        category: product?.category,
        brand: product?.brand,
        sku: product?.sku,
        weight: product?.weight,
        dimensions: product?.dimensions,
        imageUrls: product?.imageUrls,
        technicalSpecs: product?.technicalSpecs,
        images: product?.images,
        isActive: product?.isActive,
        isFeatured: !product?.isFeatured
      });
      setProducts(prev => prev?.map(p => p?.id === product?.id ? { ...p, isFeatured: !p?.isFeatured } : p));
    } catch (err) {
      setError(err?.message || 'Öne çıkarma durumu güncellenirken hata oluştu');
      console.error('Error toggling featured status:', err);
    }
  };

  const handleToggleProductStatus = async (product) => {
    try {
      await productService?.update(product?.id, {
        title: product?.title,
        description: product?.description,
        price: product?.price,
        compareAtPrice: product?.compareAtPrice,
        stockQuantity: product?.stockQuantity,
        lowStockThreshold: product?.lowStockThreshold,
        trackInventory: product?.trackInventory,
        category: product?.category,
        brand: product?.brand,
        sku: product?.sku,
        weight: product?.weight,
        dimensions: product?.dimensions,
        imageUrls: product?.imageUrls,
        technicalSpecs: product?.technicalSpecs,
        images: product?.images,
        isActive: !product?.isActive
      });
      setProducts(prev => prev?.map(p => p?.id === product?.id ? { ...p, isActive: !p?.isActive } : p));
    } catch (err) {
      setError(err?.message || 'Ürün durumu güncellenirken hata oluştu');
      console.error('Error toggling product status:', err);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    setIsLoading(true);
    setError(null);

    try {
      await adminOrderService?.updateOrderStatus(orderId, newStatus);
      await loadData();
      setShowOrderDetails(null);
    } catch (err) {
      setError(err?.message || 'Sipariş durumu güncellenirken hata oluştu');
      console.error('Error updating order status:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewOrderDetails = async (orderId) => {
    try {
      const orderDetails = await adminOrderService?.getOrderById(orderId);
      setShowOrderDetails(orderDetails);
      // Reset trackingData so inputs show the saved values from DB (via fallback), not stale state
      setTrackingData({ company: '', number: '' });
      setCancelReason('');
    } catch (err) {
      setError(err?.message || 'Sipariş detayları yüklenirken hata oluştu');
      console.error('Error loading order details:', err);
    }
  };

  const handleOpenInventoryModal = async (product) => {
    setSelectedProductForInventory(product);
    setAdjustmentForm({
      quantityChange: '',
      transactionType: 'adjustment',
      notes: ''
    });
    setShowInventoryModal(true);
    
    // Load transaction history
    try {
      const transactions = await inventoryService?.getProductTransactions(product?.id);
      setInventoryTransactions(transactions);
    } catch (err) {
      console.error('Error loading transactions:', err);
    }
  };

  const handleInventoryAdjustment = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await inventoryService?.recordAdjustment({
        productId: selectedProductForInventory?.id,
        transactionType: adjustmentForm?.transactionType,
        quantityChange: parseInt(adjustmentForm?.quantityChange),
        notes: adjustmentForm?.notes
      });

      // Reload data
      const transactions = await inventoryService?.getProductTransactions(selectedProductForInventory?.id);
      setInventoryTransactions(transactions);
      await loadData();
      await loadInventoryData();
      
      // Reset form
      setAdjustmentForm({
        quantityChange: '',
        transactionType: 'adjustment',
        notes: ''
      });
    } catch (err) {
      setError(err?.message || 'Stok güncelleme hatası');
      console.error('Error adjusting inventory:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await inventoryService?.dismissAlert(alertId);
      await loadInventoryData();
    } catch (err) {
      console.error('Error dismissing alert:', err);
    }
  };

  const handleImageFileUpload = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files?.length) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const invalidFile = files?.find(f => !allowedTypes?.includes(f?.type));
    if (invalidFile) {
      setImageUploadError('Sadece JPEG, PNG veya WebP formatında görsel yükleyebilirsiniz.');
      return;
    }
    const oversizedFile = files?.find(f => f?.size > 10 * 1024 * 1024);
    if (oversizedFile) {
      setImageUploadError('Her görsel 10MB\'dan küçük olmalıdır.');
      return;
    }

    setImageUploadLoading(true);
    setImageUploadError('');

    try {
      const uploadedImages = [];
      for (const file of files) {
        const fileExt = file?.name?.split('.')?.pop();
        const fileName = `product-${Date.now()}-${Math.random()?.toString(36)?.substring(2)}.${fileExt}`;
        const filePath = `products/${fileName}`;

        const { error: uploadError } = await supabase?.storage?.from('product-images')?.upload(filePath, file, { cacheControl: '3600', upsert: false });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase?.storage?.from('product-images')?.getPublicUrl(filePath);
        const publicUrl = urlData?.publicUrl;

        uploadedImages?.push({
          url: publicUrl,
          alt: file?.name?.replace(/\.[^/.]+$/, ''),
          isPrimary: false
        });
      }

      setProductForm(prev => {
        const existingImages = prev?.imageUrls || [];
        const newImages = uploadedImages?.map((img, idx) => ({
          ...img,
          isPrimary: existingImages?.length === 0 && idx === 0
        }));
        return {
          ...prev,
          imageUrls: [...existingImages, ...newImages]
        };
      });
      setNewImageAlt('');
      e.target.value = '';
    } catch (err) {
      setImageUploadError(err?.message || 'Görsel yüklenirken hata oluştu.');
      console.error('Image upload error:', err);
    } finally {
      setImageUploadLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    })?.format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-800' },
      processing: { label: 'İşleniyor', color: 'bg-blue-100 text-blue-800' },
      shipped: { label: 'Kargoda', color: 'bg-purple-100 text-purple-800' },
      delivered: { label: 'Teslim Edildi', color: 'bg-green-100 text-green-800' },
      cancelled: { label: 'İptal Edildi', color: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const tabs = [
    { id: 'products', label: 'Ürünler', icon: 'Package' },
    { id: 'inventory', label: 'Envanter', icon: 'BarChart3' },
    { id: 'orders', label: 'Siparişler', icon: 'ShoppingCart' },
    { id: 'categories', label: 'Kategoriler', icon: 'Tag' }
  ];

  const handleAddCategory = () => {
    const trimmed = newCategoryInput?.trim();
    if (!trimmed || categories?.includes(trimmed)) return;
    setCategories(prev => [...prev, trimmed]);
    setNewCategoryInput('');
  };

  const handleDeleteCategory = (cat) => {
    setCategories(prev => prev?.filter(c => c !== cat));
  };

  const handleAddImage = () => {
    if (!newImageUrl?.trim()) return;
    
    const newImage = {
      url: newImageUrl?.trim(),
      alt: newImageAlt?.trim() || 'Product image',
      isPrimary: productForm?.imageUrls?.length === 0
    };
    
    setProductForm({
      ...productForm,
      imageUrls: [...productForm?.imageUrls, newImage]
    });
    
    setNewImageUrl('');
    setNewImageAlt('');
  };

  const handleRemoveImage = (index) => {
    const updatedImages = productForm?.imageUrls?.filter((_, i) => i !== index);
    
    // If we removed the primary image, make the first one primary
    if (updatedImages?.length > 0 && !updatedImages?.some(img => img?.isPrimary)) {
      updatedImages[0].isPrimary = true;
    }
    
    setProductForm({
      ...productForm,
      imageUrls: updatedImages
    });
  };

  const handleSetPrimaryImage = (index) => {
    const updatedImages = productForm?.imageUrls?.map((img, i) => ({
      ...img,
      isPrimary: i === index
    }));
    
    setProductForm({
      ...productForm,
      imageUrls: updatedImages
    });
  };

  const handleAddSpec = () => {
    if (!newSpecKey?.trim() || !newSpecValue?.trim()) return;
    
    setProductForm({
      ...productForm,
      technicalSpecs: {
        ...productForm?.technicalSpecs,
        [newSpecKey?.trim()]: newSpecValue?.trim()
      }
    });
    
    setNewSpecKey('');
    setNewSpecValue('');
  };

  const handleRemoveSpec = (key) => {
    const updatedSpecs = { ...productForm?.technicalSpecs };
    delete updatedSpecs?.[key];
    
    setProductForm({
      ...productForm,
      technicalSpecs: updatedSpecs
    });
  };

  // ── Bundle Handlers ───────────────────────────────────────────────────────
  const handleAddBundleItem = (childProduct) => {
    if (!childProduct) return;
    const alreadyAdded = productForm?.bundleItems?.some(bi => bi?.productId === childProduct?.id);
    if (alreadyAdded) return;
    const newBundleItem = {
      productId: childProduct?.id,
      title: childProduct?.title,
      price: childProduct?.price,
      variants: childProduct?.variants || [],
      image: childProduct?.imageUrls?.[0]?.url || childProduct?.image || null
    };
    setProductForm(prev => ({
      ...prev,
      bundleItems: [...(prev?.bundleItems || []), newBundleItem]
    }));
    setBundleChildSearch('');
  };

  const handleRemoveBundleItem = (productId) => {
    setProductForm(prev => ({
      ...prev,
      bundleItems: (prev?.bundleItems || [])?.filter(bi => bi?.productId !== productId)
    }));
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ── Variant Handlers ──────────────────────────────────────────────────────
  const handleAddVariant = () => {
    if (!newVariant?.color?.trim() && !newVariant?.size?.trim()) return;
    const variant = {
      id: `v${Date.now()}`,
      color: newVariant?.color?.trim() || '',
      size: newVariant?.size?.trim() || '',
      stock: parseInt(newVariant?.stock) || 0
    };
    setProductForm(prev => ({
      ...prev,
      variants: [...(prev?.variants || []), variant]
    }));
    setNewVariant({ color: '', size: '', stock: '' });
  };

  const handleRemoveVariant = (variantId) => {
    setProductForm(prev => ({
      ...prev,
      variants: (prev?.variants || [])?.filter(v => v?.id !== variantId)
    }));
  };

  const handleVariantStockChange = (variantId, newStock) => {
    setProductForm(prev => ({
      ...prev,
      variants: (prev?.variants || [])?.map(v =>
        v?.id === variantId ? { ...v, stock: parseInt(newStock) || 0 } : v
      )
    }));
  };

  const handleCloneVariants = (sourceProductId) => {
    const sourceProduct = products?.find(p => p?.id === sourceProductId);
    if (!sourceProduct?.variants?.length) return;
    const clonedVariants = sourceProduct?.variants?.map(v => ({
      ...v,
      id: `v${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`,
      stock: 0
    }));
    setProductForm(prev => ({ ...prev, variants: clonedVariants }));
    setShowVariantCloneDropdown(false);
  };
  // ─────────────────────────────────────────────────────────────────────────

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleDeleteOrder = async () => {
    if (!deleteOrderConfirm) return;
    setDeleteOrderLoading(true);
    try {
      // Delete order_items first (cascade), then the order
      await supabase?.from('order_items')?.delete()?.eq('order_id', deleteOrderConfirm?.id);
      const { error } = await supabase?.from('orders')?.delete()?.eq('id', deleteOrderConfirm?.id);
      if (error) throw error;
      setDeleteOrderConfirm(null);
      showToast('Kayıt başarıyla silindi');
      await loadData();
    } catch (err) {
      showToast(err?.message || 'Sipariş silinirken hata oluştu', 'error');
    } finally {
      setDeleteOrderLoading(false);
    }
  };

  const handleSendToMysoft = async (orderId) => {
    if (!confirm("Bu siparişi Mysoft'a göndermek istediğinizden emin misiniz?")) return;
    setSendingMysoftId(orderId);
    try {
      await adminOrderService?.sendToMysoft(orderId);
      showToast("Sipariş başarıyla Mysoft'a gönderildi.");
    } catch (err) {
      showToast(`Mysoft gönderimi başarısız: ${err?.message || 'Bilinmeyen hata'}`, 'error');
    } finally {
      setSendingMysoftId(null);
    }
  };

  const handleToggleSelectOrder = (orderId) => {
    setSelectedOrders(prev =>
      prev?.includes(orderId) ? prev?.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  const handleSelectAllOrders = (e) => {
    if (e?.target?.checked) {
      setSelectedOrders(orders?.map(o => o?.id) || []);
    } else {
      setSelectedOrders([]);
    }
  };

  const handleBulkDeleteOrders = async () => {
    if (selectedOrders?.length === 0) return;
    if (!window.confirm(`Seçili ${selectedOrders?.length} siparişi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) return;

    try {
      const { error: itemsError } = await supabase
        ?.from('order_items')
        ?.delete()
        ?.in('order_id', selectedOrders);

      if (itemsError) {
        alert('Sipariş kalemleri silinemedi: ' + itemsError?.message);
        return;
      }

      const { error: orderError } = await supabase
        ?.from('orders')
        ?.delete()
        ?.in('id', selectedOrders);

      if (orderError) {
        alert('Siparişler silinemedi: ' + orderError?.message);
        return;
      }

      setOrders(prev => prev?.filter(o => !selectedOrders?.includes(o?.id)));
      const count = selectedOrders?.length;
      setSelectedOrders([]);
      setToast({ type: 'success', message: `${count} sipariş başarıyla silindi.` });
      setTimeout(() => setToast(null), 4000);
    } catch (err) {
      alert('Siparişler silinemedi: ' + err?.message);
    }
  };

  // ── Inline order-edit handlers ──
  const handleSaveTotal = async () => {
    const parsed = parseFloat(newTotal?.toString()?.replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      alert('Geçerli bir tutar girin.');
      return;
    }
    try {
      const { error } = await supabase?.from('orders')?.update({ total_amount: parsed })?.eq('id', showOrderDetails?.id);
      if (error) throw error;
      setShowOrderDetails(prev => ({ ...prev, total_amount: parsed, totalAmount: parsed }));
      setIsEditingTotal(false);
    } catch (err) {
      alert('Hata: ' + err?.message);
    }
  };

  const handleDeleteItem = async (itemId, itemTitle) => {
    if (!window.confirm(`"${itemTitle}" kalemini siparişten kaldırmak istediğinize emin misiniz?`)) return;
    setRemovingItemId(itemId);
    try {
      const { error } = await supabase?.from('order_items')?.delete()?.eq('id', itemId);
      if (error) throw error;
      setShowOrderDetails(prev => ({
        ...prev,
        items: prev?.items?.filter(i => i?.id !== itemId)
      }));
    } catch (err) {
      alert('Kalem kaldırılamadı: ' + err?.message);
    } finally {
      setRemovingItemId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Mağaza Yönetimi</h1>
          <p className="text-muted-foreground mt-2">Ürünleri, envanteri ve siparişleri yönetin</p>
        </div>

        {/* Inventory Stats Cards */}
        {inventoryStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Toplam Ürün</p>
                  <p className="text-2xl font-bold text-foreground">{inventoryStats?.totalProducts}</p>
                </div>
                <Icon name="Package" size={32} className="text-primary" />
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Düşük Stok</p>
                  <p className="text-2xl font-bold text-warning">{inventoryStats?.lowStockCount}</p>
                </div>
                <Icon name="AlertTriangle" size={32} className="text-warning" />
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Tükenen</p>
                  <p className="text-2xl font-bold text-error">{inventoryStats?.outOfStockCount}</p>
                </div>
                <Icon name="XCircle" size={32} className="text-error" />
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Sağlıklı Stok</p>
                  <p className="text-2xl font-bold text-success">{inventoryStats?.healthyStockCount}</p>
                </div>
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
            </div>
          </div>
        )}

        {/* Low Stock Alerts Banner */}
        {lowStockAlerts?.length > 0 && (
          <div className="bg-warning/10 border border-warning rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={24} className="text-warning flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">
                  {lowStockAlerts?.length} ürün düşük stokta
                </h3>
                <div className="space-y-2">
                  {lowStockAlerts?.slice(0, 3)?.map(alert => (
                    <div key={alert?.id} className="flex items-center gap-4 bg-card rounded p-2">
                      <span className="text-sm text-foreground">
                        {alert?.product?.title} - Stok: {alert?.currentStock} / Eşik: {alert?.threshold}
                      </span>
                      <button
                        onClick={() => handleDismissAlert(alert?.id)}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        Kapat
                      </button>
                    </div>
                  ))}
                </div>
                {lowStockAlerts?.length > 3 && (
                  <button
                    onClick={() => setActiveTab('inventory')}
                    className="text-sm text-primary hover:underline mt-2"
                  >
                    +{lowStockAlerts?.length - 3} daha fazla göster
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              {tab?.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg text-error">
            {error}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Kategori Yönetimi</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Ürün kategorilerini buradan yönetin. Eklediğiniz kategoriler ürün formlarında seçenek olarak görünür.
              </p>

              {/* Add new category */}
              <div className="flex gap-3 mb-6">
                <input
                  type="text"
                  value={newCategoryInput}
                  onChange={(e) => setNewCategoryInput(e?.target?.value)}
                  onKeyDown={(e) => { if (e?.key === 'Enter') { e?.preventDefault(); handleAddCategory(); } }}
                  placeholder="Yeni kategori adı..."
                  className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-card text-foreground text-sm"
                />
                <button
                  onClick={handleAddCategory}
                  disabled={!newCategoryInput?.trim() || categories?.includes(newCategoryInput?.trim())}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Icon name="Plus" size={16} />
                  Ekle
                </button>
              </div>

              {/* Category list */}
              {categories?.length === 0 ? (
                <p className="text-muted-foreground text-sm">Henüz kategori bulunmuyor.</p>
              ) : (
                <div className="space-y-2">
                  {categories?.map((cat, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-primary" />
                        <span className="text-sm font-medium text-foreground">{cat}</span>
                      </div>
                      <button
                        onClick={() => handleDeleteCategory(cat)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Kategoriyi Sil"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Düşük Stok Uyarıları</h2>
              {lowStockAlerts?.length === 0 ? (
                <p className="text-muted-foreground">Tüm ürünler yeterli stok seviyesinde</p>
              ) : (
                <div className="space-y-3">
                  {lowStockAlerts?.map(alert => (
                    <div key={alert?.id} className="flex items-center gap-4 p-4 bg-muted rounded-lg border border-border">
                      {alert?.product?.image && (
                        <img
                          src={alert?.product?.image}
                          alt={alert?.product?.title}
                          className="w-16 h-16 object-cover rounded"
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="font-medium text-foreground">{alert?.product?.title}</h3>
                        <p className="text-sm text-muted-foreground">SKU: {alert?.product?.sku || 'N/A'}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-sm">
                            Mevcut Stok: <span className="font-semibold text-warning">{alert?.currentStock}</span>
                          </span>
                          <span className="text-sm">
                            Eşik: <span className="font-semibold">{alert?.threshold}</span>
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            const product = products?.find(p => p?.id === alert?.productId);
                            if (product) handleOpenInventoryModal(product);
                          }}
                          variant="outline"
                          size="sm"
                        >
                          Stok Ekle
                        </Button>
                        <button
                          onClick={() => handleDismissAlert(alert?.id)}
                          className="px-3 py-1 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Icon name="X" size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-foreground">Ürün Yönetimi</h3>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={handleAddProduct}
                disabled={isLoading}
              >
                Yeni Ürün Ekle
              </Button>
              <Button
                variant="outline"
                iconName="Sparkles"
                iconPosition="left"
                onClick={() => setShowSmartAddModal(true)}
              >
                Link ile Akıllı Ekle
              </Button>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
              </div>
            ) : products?.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-lg border border-border">
                <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Henüz ürün bulunmuyor</p>
              </div>
            ) : (
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="mb-4 max-w-md px-4 pt-4">
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm shadow-sm"
                      placeholder="Ürün adı veya kategoriye göre ara..."
                      value={productSearchTerm}
                      onChange={(e) => setProductSearchTerm(e?.target?.value)}
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Ürün</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Fiyat</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Stok</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Kategori</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Durum</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(products || [])?.filter(product => {
                          const term = productSearchTerm?.toLowerCase();
                          return ((product?.title || "")?.toLowerCase()?.includes(term) || (product?.category || "")?.toLowerCase()?.includes(term));
                        })?.map(product => (
                        <tr key={product?.id} className="hover:bg-muted/50">
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              {product?.image ? (
                                <img
                                  src={product?.image}
                                  alt={product?.title}
                                  className="w-12 h-12 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                                  <Icon name="Image" size={20} className="text-muted-foreground" />
                                </div>
                              )}
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-foreground">{product?.title}</p>
                                </div>
                                <p className="text-sm text-muted-foreground line-clamp-1">
                                  {product?.description}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div>
                              <p className="font-medium text-foreground">{formatPrice(product?.price)}</p>
                              {product?.compareAtPrice && (
                                <p className="text-sm text-muted-foreground line-through">
                                  {formatPrice(product?.compareAtPrice)}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-medium ${
                                product?.stockQuantity === 0 ? 'text-error' :
                                product?.stockQuantity <= (product?.lowStockThreshold || 10) ? 'text-warning': 'text-success'
                              }`}>
                                {product?.stockQuantity}
                              </span>
                              {product?.trackInventory && product?.stockQuantity <= (product?.lowStockThreshold || 10) && (
                                <Icon name="AlertTriangle" size={16} className="text-warning" />
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-foreground">
                            <select
                              value={product?.category || ''}
                              onChange={async (e) => {
                                const newCat = e?.target?.value;
                                setProducts(prev => prev?.map(p => p?.id === product?.id ? { ...p, category: newCat } : p));
                                try {
                                  await productService?.update(product?.id, { category: newCat });
                                } catch (err) {
                                  console.error('Category update error:', err);
                                  setProducts(prev => prev?.map(p => p?.id === product?.id ? { ...p, category: product?.category } : p));
                                }
                              }}
                              className="bg-transparent border-b border-dashed border-gray-300 cursor-pointer hover:bg-gray-50 focus:outline-none text-sm py-0.5 pr-6 rounded"
                            >
                              <option value="">—</option>
                              {categories?.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-4">{getStatusBadge(product?.status)}</td>
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleToggleProductStatus(product)}
                                className={`transition-colors ${product?.isActive
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                }`}
                                title={product?.isActive ? 'Pasife al' : 'Aktife al'}
                              >
                                {product?.isActive ? 'Aktif' : 'Pasif'}
                              </button>
                              <button
                                onClick={() => handleOpenInventoryModal(product)}
                                className="text-primary hover:text-primary/80"
                                title="Stok Yönetimi"
                              >
                                <Icon name="BarChart3" size={18} />
                              </button>
                              <button
                                onClick={() => handleEditProduct(product)}
                                className="text-primary hover:text-primary/80"
                              >
                                <Icon name="Edit" size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(product?.id)}
                                className="text-error hover:text-error/80"
                              >
                                <Icon name="Trash2" size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-foreground">Sipariş Yönetimi</h3>
              {selectedOrders?.length > 0 && (
                <button
                  onClick={handleBulkDeleteOrders}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  <Icon name="Trash2" size={16} />
                  Seçilenleri Sil ({selectedOrders?.length})
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
              </div>
            ) : orders?.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-lg border border-border">
                <Icon name="ShoppingCart" size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Henüz sipariş bulunmuyor</p>
              </div>
            ) : (
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={orders?.length > 0 && selectedOrders?.length === orders?.length}
                            onChange={handleSelectAllOrders}
                            className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Sipariş No</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Müşteri</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tutar</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Ödeme Yöntemi</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Ödeme Durumu</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Durum</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {orders?.map(order => (
                        <tr key={order?.id} className={`hover:bg-muted/50 ${selectedOrders?.includes(order?.id) ? 'bg-red-50' : ''}`}>
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={selectedOrders?.includes(order?.id)}
                              onChange={() => handleToggleSelectOrder(order?.id)}
                              className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-4">
                            <p className="font-mono text-sm text-foreground">
                              #{order?.id?.slice(0, 8)}
                            </p>
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-sm text-foreground">
                              {(() => {
                                // Always prefer the direct full_name column on the order first
                                let name = (order?.full_name && order?.full_name?.trim() !== '') ? order?.full_name?.trim() : null;
                                if (!name) name = order?.user_profile?.full_name || order?.profiles?.full_name || order?.user?.full_name || null;
                                if (!name && order?.shippingAddress) {
                                  try {
                                    let addrObj = typeof order?.shippingAddress === 'string' ? JSON.parse(order?.shippingAddress) : order?.shippingAddress;
                                    name = addrObj?.ContactName || addrObj?.AdSoyad || addrObj?.full_name || addrObj?.fullName || (addrObj?.firstName ? `${addrObj?.firstName || ''} ${addrObj?.lastName || ''}`?.trim() : null);
                                  } catch(e){}
                                }
                                if (!name && order?.shipping_address) {
                                  try {
                                    let addrObj = typeof order?.shipping_address === 'string' ? JSON.parse(order?.shipping_address) : order?.shipping_address;
                                    name = addrObj?.ContactName || addrObj?.AdSoyad || addrObj?.full_name || addrObj?.fullName || (addrObj?.firstName ? `${addrObj?.firstName || ''} ${addrObj?.lastName || ''}`?.trim() : null);
                                  } catch(e){}
                                }
                                if (!name || !name?.trim()) return 'İsimsiz Müşteri';
                                return name?.trim();
                              })()}
                            </p>
                          </td>
                          <td className="px-4 py-4">
                            <p className="font-medium text-foreground">{formatPrice(order?.totalAmount)}</p>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {(order?.payment_method || order?.paymentMethod) === 'bank_transfer' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-800 font-medium">
                                EFT / Havale
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 font-medium">
                                Kredi Kartı
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {(() => {
                              const ps = order?.payment_status || order?.paymentStatus || 'pending';
                              if (ps === 'paid' || ps === 'completed') {
                                return (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 font-semibold">
                                    ✅ Ödendi
                                  </span>
                                );
                              }
                              if (ps === 'failed') {
                                return (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 font-semibold">
                                    ❌ Başarısız
                                  </span>
                                );
                              }
                              return (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800 font-semibold cursor-pointer hover:bg-amber-200 transition-colors"
                                  title="Ödendi olarak işaretlemek için tıklayın"
                                  onClick={async (e) => {
                                    e?.stopPropagation();
                                    if (window.confirm('Bu siparişi ödendi olarak işaretlemek istediğinize emin misiniz?')) {
                                      await supabase?.from('orders')?.update({ payment_status: 'paid' })?.eq('id', order?.id);
                                      loadData();
                                    }
                                  }}
                                >⏳ Bekliyor 
                                                                  </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-4">{getStatusBadge(order?.status)}</td>
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleViewOrderDetails(order?.id)}
                                className="px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-smooth"
                              >
                                Detaylar
                              </button>
                              <button
                                onClick={() => setDeleteOrderConfirm(order)}
                                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                                title="Siparişi Sil"
                              >
                                <Icon name="Trash2" size={16} />
                              </button>
                              <button
                                onClick={() => handleSendToMysoft(order?.id)}
                                disabled={sendingMysoftId === order?.id}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="Mysoft'a Gönder"
                              >
                                {sendingMysoftId === order?.id ? (
                                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Icon name="Send" size={12} />
                                )}
                                Mysoft
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Product Modal */}
        {showProductModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-card rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-foreground">
                    {editingProduct ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
                  </h2>
                  <button
                    onClick={() => setShowProductModal(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="X" size={24} />
                  </button>
                </div>

                <form onSubmit={handleSaveProduct} className="space-y-6">
                  <Input
                    label="Ürün Adı"
                    value={productForm?.title}
                    onChange={(e) => setProductForm({ ...productForm, title: e?.target?.value })}
                    placeholder="Ürün adını girin"
                    required
                  />

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Açıklama</label>
                    <textarea
                      value={productForm?.description}
                      onChange={(e) => setProductForm({ ...productForm, description: e?.target?.value })}
                      placeholder="Ürün açıklamasını girin"
                      rows={3}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>

                  {/* ── PRICING SECTION ── */}
                  {!productForm?.isBundle ? (
                    <div className="p-4 border border-border rounded-xl bg-card space-y-4">
                      <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <span>💰</span> Fiyatlandırma
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          label="Normal Satış Fiyatı (₺)"
                          type="number"
                          step="0.01"
                          value={productForm?.price}
                          onChange={(e) => setProductForm({ ...productForm, price: e?.target?.value })}
                          placeholder="0.00"
                          required
                        />
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Kampanyalı Satış Fiyatı (₺)
                            <span className="ml-1 text-xs font-normal text-muted-foreground">(opsiyonel)</span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={productForm?.discountedPrice}
                            onChange={(e) => setProductForm({ ...productForm, discountedPrice: e?.target?.value })}
                            placeholder="İndirimli fiyat..."
                            className="w-full px-3 py-2 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 bg-card text-foreground text-sm"
                          />
                          {productForm?.discountedPrice && parseFloat(productForm?.discountedPrice) > 0 && parseFloat(productForm?.price) > 0 && parseFloat(productForm?.discountedPrice) < parseFloat(productForm?.price) && (
                            <p className="text-xs text-emerald-600 mt-1 font-medium">
                              ✅ %{Math.round((1 - parseFloat(productForm?.discountedPrice) / parseFloat(productForm?.price)) * 100)} indirim uygulanacak. Orijinal fiyat üstü çizili gösterilecek.
                            </p>
                          )}
                          {productForm?.discountedPrice && parseFloat(productForm?.discountedPrice) >= parseFloat(productForm?.price) && (
                            <p className="text-xs text-red-500 mt-1">⚠️ Kampanyalı fiyat normal fiyattan düşük olmalıdır.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Bundle auto-pricing display */
                    ((() => {
                      const bundleSum = (productForm?.bundleItems || [])?.reduce((sum, bi) => sum + (parseFloat(bi?.price) || 0), 0);
                      const discountPct = parseFloat(productForm?.bundleDiscountPercentage) || 0;
                      const finalBundlePrice = discountPct > 0 ? bundleSum * (1 - discountPct / 100) : bundleSum;
                      return (
                        <div className="p-4 border-2 border-emerald-200 rounded-xl bg-emerald-50 space-y-2">
                          <p className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
                            <span>🤖</span> Otomatik Paket Fiyatı (Salt-Okunur)
                          </p>
                          <div className="flex items-center gap-4">
                            <div>
                              <p className="text-xs text-gray-500">Toplam Baz Fiyat</p>
                              <p className="text-lg font-bold text-gray-700">{bundleSum?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}₺</p>
                            </div>
                            {discountPct > 0 && (
                              <>
                                <span className="text-gray-400 text-xl">→</span>
                                <div>
                                  <p className="text-xs text-gray-500">%{discountPct} İndirim Sonrası</p>
                                  <p className="text-xl font-bold text-emerald-700">{finalBundlePrice?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}₺</p>
                                </div>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-emerald-600">Bu fiyat paket ürünleri ve indirim oranına göre otomatik hesaplanır. Manuel giriş gerekmez.</p>
                        </div>
                      );
                    })())
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Alış Fiyatı (₺)"
                      type="number"
                      step="0.01"
                      value={productForm?.purchasePrice}
                      onChange={(e) => setProductForm({ ...productForm, purchasePrice: e?.target?.value })}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">KDV Oranı (%)</label>
                      <select
                        value={productForm?.vatRate}
                        onChange={(e) => setProductForm({ ...productForm, vatRate: e?.target?.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-card text-foreground"
                      >
                        <option value="1">%1</option>
                        <option value="10">%10</option>
                        <option value="20">%20</option>
                      </select>
                    </div>
                    <Input
                      label="Stok Kodu (SKU)"
                      value={productForm?.sku}
                      onChange={(e) => setProductForm({ ...productForm, sku: e?.target?.value })}
                      placeholder="Örn: ATP-SUP-001"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Stok Miktarı</label>
                      {(productForm?.variants || [])?.length > 0 ? (
                        <div className="px-3 py-2 border border-border rounded-lg bg-muted text-muted-foreground text-sm flex items-center gap-2">
                          <Icon name="Lock" size={14} />
                          <span>{(productForm?.variants || [])?.reduce((sum, v) => sum + (parseInt(v?.stock) || 0), 0)} adet (varyantlardan)</span>
                        </div>
                      ) : (
                        <input
                          type="number"
                          value={productForm?.stockQuantity}
                          onChange={(e) => setProductForm({ ...productForm, stockQuantity: e?.target?.value })}
                          required
                          min="0"
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-card text-foreground text-sm"
                        />
                      )}
                    </div>

                    <Input
                      label="Ağırlık / Desi (KG)"
                      type="number"
                      step="0.01"
                      value={productForm?.weight}
                      onChange={(e) => setProductForm({ ...productForm, weight: e?.target?.value })}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Düşük Stok Eşiği"
                      type="number"
                      value={productForm?.lowStockThreshold}
                      onChange={(e) => setProductForm({ ...productForm, lowStockThreshold: e?.target?.value })}
                      required
                      min="0"
                      placeholder="10"
                    />
                    <Input
                      label="Marka"
                      value={productForm?.brand}
                      onChange={(e) => setProductForm({ ...productForm, brand: e?.target?.value })}
                      placeholder="Ürün markası"
                    />
                  </div>

                  {/* ── Bundle Toggle Section ── */}
                  <div className="p-4 border-2 border-orange-200 rounded-xl bg-orange-50 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">📦</span>
                        <div>
                          <p className="text-sm font-bold text-gray-800">Bu bir Paket/Set Üründür</p>
                          <p className="text-xs text-gray-500">Birden fazla ürünü tek paket olarak sat. Fatura child ürünlere patlatılır.</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setProductForm(prev => ({ ...prev, isBundle: !prev?.isBundle }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          productForm?.isBundle ? 'bg-orange-500' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          productForm?.isBundle ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {productForm?.isBundle && (
                      <div className="space-y-4 pt-2 border-t border-orange-200">
                        {/* Bundle Discount */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Paket İndirim Oranı (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={productForm?.bundleDiscountPercentage}
                            onChange={(e) => setProductForm(prev => ({ ...prev, bundleDiscountPercentage: e?.target?.value }))}
                            placeholder="Örn: 15"
                            className="w-full px-3 py-2 text-sm border border-orange-300 rounded-lg bg-white text-foreground focus:ring-2 focus:ring-orange-300 focus:outline-none"
                          />
                          <p className="text-xs text-gray-500 mt-1">Bu oran faturada her child ürünün birim fiyatına uygulanır.</p>
                        </div>

                        {/* Child Product Search & Add */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Pakete Ürün Ekle</label>
                          <div className="relative">
                            <input
                              type="text"
                              value={bundleChildSearch}
                              onChange={(e) => setBundleChildSearch(e?.target?.value)}
                              placeholder="Ürün adıyla ara..."
                              className="w-full px-3 py-2 text-sm border border-orange-300 rounded-lg bg-white text-foreground focus:ring-2 focus:ring-orange-300 focus:outline-none"
                            />
                            {bundleChildSearch?.trim()?.length > 0 && (
                              <div className="absolute z-10 w-full mt-1 bg-white border border-orange-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                {products
                                  ?.filter(p =>
                                    p?.id !== editingProduct?.id &&
                                    !p?.isBundle && !p?.is_bundle &&
                                    (p?.title || '')?.toLowerCase()?.includes(bundleChildSearch?.toLowerCase())
                                  )
                                  ?.slice(0, 8)
                                  ?.map(p => (
                                    <button
                                      key={p?.id}
                                      type="button"
                                      onClick={() => handleAddBundleItem(p)}
                                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-orange-50 text-left transition-colors"
                                    >
                                      {p?.imageUrls?.[0]?.url && (
                                        <img src={p?.imageUrls?.[0]?.url} alt={p?.title} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate">{p?.title}</p>
                                        <p className="text-xs text-gray-500">{p?.price?.toLocaleString('tr-TR')}₺</p>
                                      </div>
                                      <Icon name="Plus" size={14} className="text-orange-500 flex-shrink-0" />
                                    </button>
                                  ))}
                                {products?.filter(p =>
                                  p?.id !== editingProduct?.id &&
                                  !p?.isBundle && !p?.is_bundle &&
                                  (p?.title || '')?.toLowerCase()?.includes(bundleChildSearch?.toLowerCase())
                                )?.length === 0 && (
                                  <p className="px-3 py-2 text-sm text-gray-500">Ürün bulunamadı</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Bundle Items List */}
                        {(productForm?.bundleItems || [])?.length > 0 ? (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-gray-700">Paketteki Ürünler ({productForm?.bundleItems?.length})</p>
                            {(productForm?.bundleItems || [])?.map((bi, idx) => (
                              <div key={bi?.productId} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-orange-200">
                                <span className="text-xs font-bold text-orange-500 w-5">{idx + 1}</span>
                                {bi?.image && (
                                  <img src={bi?.image} alt={bi?.title} className="w-10 h-10 rounded object-cover flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-800 truncate">{bi?.title}</p>
                                  <p className="text-xs text-gray-500">
                                    {bi?.price?.toLocaleString('tr-TR')}₺
                                    {parseFloat(productForm?.bundleDiscountPercentage) > 0 && (
                                      <span className="ml-2 text-green-600 font-semibold">
                                        → {(bi?.price * (1 - parseFloat(productForm?.bundleDiscountPercentage) / 100))?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}₺ (indirimli)
                                      </span>
                                    )}
                                    {bi?.variants?.length > 0 && (
                                      <span className="ml-2 text-blue-500">• {bi?.variants?.length} varyant</span>
                                    )}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveBundleItem(bi?.productId)}
                                  className="p-1.5 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                                >
                                  <Icon name="Trash2" size={14} className="text-red-500" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 italic">Henüz pakete ürün eklenmedi. Yukarıdan arama yaparak ekleyin.</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Variants Builder Section ── */}
                  <div className="space-y-4 p-4 border-2 border-primary/20 rounded-xl bg-primary/5">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-bold text-gray-700">Ürün Seçenekleri (Renk / Beden)</h3>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setShowVariantCloneDropdown(!showVariantCloneDropdown)}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
                        >
                          📋 Başka Üründen Kopyala
                        </button>
                        <span className="text-xs text-muted-foreground bg-white border border-border px-2 py-1 rounded-full">İsteğe Bağlı</span>
                      </div>
                    </div>
                    {showVariantCloneDropdown && (() => {
                      const productsWithVariants = products?.filter(p => p?.variants && p?.variants?.length > 0 && p?.id !== editingProduct?.id);
                      return (
                        <div className="bg-white border border-blue-200 rounded-lg p-3 shadow-sm">
                          <p className="text-xs text-muted-foreground mb-2">Varyantları kopyalanacak ürünü seçin (stoklar 0 olarak ayarlanır):</p>
                          <select
                            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-blue-300 focus:outline-none"
                            defaultValue=""
                            onChange={(e) => { if (e?.target?.value) handleCloneVariants(e?.target?.value); }}
                          >
                            <option value="" disabled>Kopyalanacak ürünü seçin...</option>
                            {productsWithVariants?.length > 0 ? productsWithVariants?.map(p => (
                              <option key={p?.id} value={p?.id}>
                                {p?.title} ({p?.variants?.length} varyant)
                              </option>
                            )) : (
                              <option value="" disabled>Varyantı olan başka ürün yok</option>
                            )}
                          </select>
                        </div>
                      );
                    })()}
                    <p className="text-xs text-muted-foreground">
                      Renk ve beden gibi seçenekler ekleyin. Seçenek eklenirse ana stok otomatik hesaplanır.
                    </p>

                    {/* Existing Variants List */}
                    {(productForm?.variants || [])?.length > 0 && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-12 gap-2 px-3 py-1">
                          <span className="col-span-4 text-xs font-semibold text-muted-foreground uppercase">Renk</span>
                          <span className="col-span-3 text-xs font-semibold text-muted-foreground uppercase">Beden</span>
                          <span className="col-span-3 text-xs font-semibold text-muted-foreground uppercase">Stok</span>
                          <span className="col-span-2"></span>
                        </div>
                        {(productForm?.variants || [])?.map(variant => (
                          <div key={variant?.id} className="grid grid-cols-12 gap-2 items-center p-3 bg-white rounded-lg border border-border">
                            <div className="col-span-4 flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-primary/40 flex-shrink-0" />
                              <span className="text-sm font-medium text-foreground truncate">{variant?.color || '—'}</span>
                            </div>
                            <div className="col-span-3">
                              <span className="text-sm text-foreground">{variant?.size || '—'}</span>
                            </div>
                            <div className="col-span-3">
                              <input
                                type="number"
                                min="0"
                                value={variant?.stock}
                                onChange={(e) => handleVariantStockChange(variant?.id, e?.target?.value)}
                                className="w-full px-2 py-1 text-sm border border-border rounded-md bg-card text-foreground focus:ring-1 focus:ring-primary/30"
                              />
                            </div>
                            <div className="col-span-2 flex justify-end">
                              <button
                                type="button"
                                onClick={() => handleRemoveVariant(variant?.id)}
                                className="p-1.5 hover:bg-red-50 rounded transition-colors"
                                title="Sil"
                              >
                                <Icon name="Trash2" size={14} className="text-red-500" />
                              </button>
                            </div>
                          </div>
                        ))}
                        <div className="flex items-center justify-between px-3 py-2 bg-primary/10 rounded-lg border border-primary/20">
                          <span className="text-xs font-semibold text-foreground">Toplam Stok (Otomatik)</span>
                          <span className="text-sm font-bold text-primary">
                            {(productForm?.variants || [])?.reduce((sum, v) => sum + (parseInt(v?.stock) || 0), 0)} adet
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Add New Variant Row */}
                    <div className="p-3 border border-dashed border-primary/30 rounded-lg space-y-3 bg-white">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">+ Yeni Seçenek Ekle</p>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Renk</label>
                          <input
                            type="text"
                            value={newVariant?.color}
                            onChange={(e) => setNewVariant({ ...newVariant, color: e?.target?.value })}
                            placeholder="Renk (Örn: Mavi)"
                            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Beden</label>
                          <input
                            type="text"
                            value={newVariant?.size}
                            onChange={(e) => setNewVariant({ ...newVariant, size: e?.target?.value })}
                            placeholder="Beden (Örn: L veya 42)"
                            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Stok</label>
                          <input
                            type="number"
                            min="0"
                            value={newVariant?.stock}
                            onChange={(e) => setNewVariant({ ...newVariant, stock: e?.target?.value })}
                            placeholder="Stok"
                            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddVariant}
                        disabled={!newVariant?.color?.trim() && !newVariant?.size?.trim()}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Icon name="Plus" size={16} />
                        Seçenek Ekle
                      </button>
                    </div>
                  </div>

                  {/* Category Select */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Kategori</label>
                    <select
                      value={productForm?.category}
                      onChange={(e) => setProductForm({ ...productForm, category: e?.target?.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-card text-foreground"
                    >
                      <option value="" disabled>Kategori Seçiniz</option>
                      {categories?.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  {/* Multiple Images Section */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Ürün Görselleri</label>
                    
                    {/* Image List */}
                    {productForm?.imageUrls?.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {productForm?.imageUrls?.map((img, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                            <img 
                              src={img?.url} 
                              alt={img?.alt} 
                              className="w-16 h-16 rounded object-cover"
                              onError={(e) => {
                                e.target.src = '/assets/images/no_image.png';
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{img?.url}</p>
                              <p className="text-xs text-muted-foreground truncate">{img?.alt}</p>
                              {img?.isPrimary && (
                                <span className="inline-block mt-1 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">
                                  Ana Görsel
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {!img?.isPrimary && (
                                <button
                                  type="button"
                                  onClick={() => handleSetPrimaryImage(index)}
                                  className="p-1.5 hover:bg-background rounded transition-smooth"
                                  title="Ana görsel yap"
                                >
                                  <Icon name="Star" size={16} className="text-muted-foreground" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(index)}
                                className="p-1.5 hover:bg-red-50 rounded transition-smooth"
                                title="Sil"
                              >
                                <Icon name="Trash2" size={16} className="text-red-600" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add New Image */}
                    <div className="space-y-2">
                      <Input
                        label="Görsel Açıklaması (Alt Text)"
                        value={newImageAlt}
                        onChange={(e) => setNewImageAlt(e?.target?.value)}
                        placeholder="Ürün görseli açıklaması"
                      />

                      {/* URL Input */}
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">Görsel URL ile Ekle</label>
                        <Input
                          label="Görsel URL"
                          value={newImageUrl}
                          onChange={(e) => setNewImageUrl(e?.target?.value)}
                          placeholder="https://example.com/image.jpg"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleAddImage}
                          iconName="Plus"
                          iconPosition="left"
                          className="w-full"
                          disabled={!newImageUrl?.trim()}
                        >
                          URL ile Ekle
                        </Button>
                      </div>

                      {/* File Upload */}
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">veya Bilgisayardan Yükle</label>
                        <label
                          className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                            imageUploadLoading
                              ? 'border-muted bg-muted/30 cursor-not-allowed' :'border-border hover:border-primary hover:bg-primary/5'
                          }`}
                        >
                          {imageUploadLoading ? (
                            <div className="flex flex-col items-center gap-1">
                              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              <span className="text-xs text-muted-foreground">Yükleniyor...</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              <Icon name="Upload" size={20} className="text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">Görsel seçmek için tıklayın</span>
                              <span className="text-xs text-muted-foreground/70">Birden fazla seçebilirsiniz — JPEG, PNG, WebP — maks. 10MB</span>
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            multiple
                            className="hidden"
                            onChange={handleImageFileUpload}
                            disabled={imageUploadLoading}
                          />
                        </label>
                        {imageUploadError && (
                          <p className="text-xs text-red-500">{imageUploadError}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Technical Specifications Section */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Teknik Özellikler</label>
                    
                    {/* Specs List */}
                    {Object.keys(productForm?.technicalSpecs || {})?.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {Object.entries(productForm?.technicalSpecs)?.map(([key, value]) => (
                          <div key={key} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                            <div className="flex-1 grid grid-cols-2 gap-3">
                              <p className="text-sm font-medium text-foreground">{key}</p>
                              <p className="text-sm text-muted-foreground">{value}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveSpec(key)}
                              className="p-1.5 hover:bg-red-50 rounded transition-smooth"
                              title="Sil"
                            >
                              <Icon name="Trash2" size={16} className="text-red-600" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add New Spec */}
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        label="Özellik Adı"
                        value={newSpecKey}
                        onChange={(e) => setNewSpecKey(e?.target?.value)}
                        placeholder="Örn: Malzeme"
                      />
                      <Input
                        label="Değer"
                        value={newSpecValue}
                        onChange={(e) => setNewSpecValue(e?.target?.value)}
                        placeholder="Örn: Karbon Fiber"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddSpec}
                      iconName="Plus"
                      iconPosition="left"
                      className="w-full mt-2"
                      disabled={!newSpecKey?.trim() || !newSpecValue?.trim()}
                    >
                      Özellik Ekle
                    </Button>
                  </div>

                  {/* Inventory Management Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-foreground">Envanter Yönetimi</h3>
                    
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="trackInventory"
                        checked={productForm?.trackInventory}
                        onChange={(e) => setProductForm({ ...productForm, trackInventory: e?.target?.checked })}
                        className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                      />
                      <label htmlFor="trackInventory" className="text-sm text-foreground">
                        Envanter takibini etkinleştir
                      </label>
                    </div>

                    {productForm?.trackInventory && (
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          label="Stok Miktarı"
                          type="number"
                          value={productForm?.stockQuantity}
                          onChange={(e) => setProductForm({ ...productForm, stockQuantity: e?.target?.value })}
                          required
                          min="0"
                        />
                        <Input
                          label="Düşük Stok Eşiği"
                          type="number"
                          value={productForm?.lowStockThreshold}
                          onChange={(e) => setProductForm({ ...productForm, lowStockThreshold: e?.target?.value })}
                          required
                          min="0"
                          placeholder="10"
                        />
                      </div>
                    )}
                  </div>

                  {/* Featured Toggle */}
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                          <Icon name="Star" size={16} className={productForm?.isFeatured ? 'fill-yellow-500 text-yellow-500' : 'text-yellow-600'} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">Öne Çıkan Ürün</p>
                          <p className="text-xs text-muted-foreground">Vitrin sıralamasında stoğu olan ürünler arasında en üste çıkar</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setProductForm({ ...productForm, isFeatured: !productForm?.isFeatured })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${productForm?.isFeatured ? 'bg-yellow-500' : 'bg-muted'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${productForm?.isFeatured ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      variant="default"
                      className="flex-1"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Kaydediliyor...' : editingProduct ? 'Güncelle' : 'Oluştur'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowProductModal(false)}
                      disabled={isLoading}
                    >
                      İptal
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Inventory Management Modal */}
        {showInventoryModal && selectedProductForInventory && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">
                      Stok Yönetimi
                    </h2>
                    <p className="text-muted-foreground">{selectedProductForInventory?.title}</p>
                  </div>
                  <button
                    onClick={() => setShowInventoryModal(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="X" size={24} />
                  </button>
                </div>

                {/* Current Stock Info */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-background border border-border rounded-lg p-4">
                    <p className="text-sm text-muted-foreground mb-1">Mevcut Stok</p>
                    <p className="text-2xl font-bold text-foreground">{selectedProductForInventory?.stockQuantity}</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-4">
                    <p className="text-sm text-muted-foreground mb-1">Düşük Stok Eşiği</p>
                    <p className="text-2xl font-bold text-warning">{selectedProductForInventory?.lowStockThreshold || 10}</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-4">
                    <p className="text-sm text-muted-foreground mb-1">Durum</p>
                    <p className={`text-lg font-semibold ${
                      selectedProductForInventory?.stockQuantity === 0 ? 'text-error' :
                      selectedProductForInventory?.stockQuantity <= (selectedProductForInventory?.lowStockThreshold || 10) ? 'text-warning': 'text-success'
                    }`}>
                      {selectedProductForInventory?.stockQuantity === 0 ? 'Tükendi' :
                       selectedProductForInventory?.stockQuantity <= (selectedProductForInventory?.lowStockThreshold || 10) ? 'Düşük': 'Sağlıklı'}
                    </p>
                  </div>
                </div>

                {/* Adjustment Form */}
                <form onSubmit={handleInventoryAdjustment} className="mb-6 p-4 bg-background border border-border rounded-lg">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Stok Güncelleme</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        İşlem Tipi
                      </label>
                      <select
                        value={adjustmentForm?.transactionType}
                        onChange={(e) => setAdjustmentForm({ ...adjustmentForm, transactionType: e?.target?.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground"
                        required
                      >
                        <option value="adjustment">Manuel Düzeltme</option>
                        <option value="restock">Yeniden Stoklama</option>
                        <option value="return">İade</option>
                        <option value="damage">Hasar/Kayıp</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Miktar Değişimi
                      </label>
                      <input
                        type="number"
                        value={adjustmentForm?.quantityChange}
                        onChange={(e) => setAdjustmentForm({ ...adjustmentForm, quantityChange: e?.target?.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground"
                        placeholder="+10 veya -5"
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Pozitif: Ekleme, Negatif: Çıkarma
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Notlar
                      </label>
                      <input
                        type="text"
                        value={adjustmentForm?.notes}
                        onChange={(e) => setAdjustmentForm({ ...adjustmentForm, notes: e?.target?.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground"
                        placeholder="İsteğe bağlı"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="mt-4"
                  >
                    {isLoading ? 'Güncelleniyor...' : 'Stok Güncelle'}
                  </Button>
                </form>

                {/* Transaction History */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">İşlem Geçmişi</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {inventoryTransactions?.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">Henüz işlem yok</p>
                    ) : (
                      inventoryTransactions?.map(transaction => (
                        <div key={transaction?.id} className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                transaction?.transactionType === 'sale' ? 'bg-error/10 text-error' :
                                transaction?.transactionType === 'restock' ? 'bg-success/10 text-success' :
                                transaction?.transactionType === 'return' ? 'bg-primary/10 text-primary' :
                                transaction?.transactionType === 'damage'? 'bg-error/10 text-error' : 'bg-muted text-muted-foreground'
                              }`}>
                                {transaction?.transactionType === 'sale' ? 'Satış' :
                                 transaction?.transactionType === 'restock' ? 'Stoklama' :
                                 transaction?.transactionType === 'return' ? 'İade' :
                                 transaction?.transactionType === 'damage'? 'Hasar' : 'Düzeltme'}
                              </span>
                              <span className={`font-semibold ${
                                transaction?.quantityChange > 0 ? 'text-success' : 'text-error'
                              }`}>
                                {transaction?.quantityChange > 0 ? '+' : ''}{transaction?.quantityChange}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {transaction?.quantityBefore} → {transaction?.quantityAfter}
                            </p>
                            {transaction?.notes && (
                              <p className="text-xs text-muted-foreground mt-1">{transaction?.notes}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">
                              {new Date(transaction?.createdAt)?.toLocaleDateString('tr-TR')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(transaction?.createdAt)?.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                            {transaction?.createdByProfile && (
                              <p className="text-xs text-muted-foreground">
                                {transaction?.createdByProfile?.fullName}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Order Details Modal */}
        {showOrderDetails && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            {console.log("DEBUG: Modal Order Object:", showOrderDetails)}
            <div className="bg-card rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-foreground">
                    Sipariş Detayları
                  </h3>
                  <button
                    onClick={() => setShowOrderDetails(null)}
                    className="p-2 hover:bg-muted rounded-lg transition-smooth"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Order Info */}
                  <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                    <div>
                      <p className="text-sm text-muted-foreground">Sipariş No</p>
                      <p className="font-mono text-sm font-medium">#{showOrderDetails?.id?.slice(0, 8)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Tarih</p>
                      <p className="text-sm font-medium">{formatDate(showOrderDetails?.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Toplam Tutar</p>
                      {isEditingTotal ? (
                        <div className="flex items-center gap-2 bg-yellow-50 p-1 rounded border border-yellow-200 mt-1">
                          <input
                            type="number"
                            value={newTotal}
                            onChange={(e) => setNewTotal(e?.target?.value)}
                            className="border border-gray-300 rounded px-3 py-1.5 text-sm min-w-[160px] w-48 flex-shrink-0 focus:outline-none focus:border-blue-500 font-bold"
                          />
                          <button
                            type="button"
                            onClick={handleSaveTotal}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1.5 rounded font-medium"
                          >
                            Kaydet
                          </button>
                          <button
                            type="button"
                            onClick={() => setIsEditingTotal(false)}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs px-2 py-1.5 rounded font-medium"
                          >
                            İptal
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-lg font-semibold text-primary">
                            {formatPrice(showOrderDetails?.totalAmount || showOrderDetails?.total_amount)}
                          </p>
                          <button
                            type="button"
                            onClick={() => { setNewTotal(showOrderDetails?.totalAmount || showOrderDetails?.total_amount || 0); setIsEditingTotal(true); }}
                            className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1 bg-blue-50 px-2 py-1 rounded border border-blue-200 font-semibold"
                          >
                            ✏️ Tutarı Düzenle
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Durum</p>
                      {getStatusBadge(showOrderDetails?.status)}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Ödeme Yöntemi</p>
                      <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-medium ${
                        (showOrderDetails?.payment_method || showOrderDetails?.paymentMethod) === 'bank_transfer' ?'bg-indigo-100 text-indigo-800' :'bg-blue-100 text-blue-800'
                      }`}>
                        {(showOrderDetails?.payment_method || showOrderDetails?.paymentMethod) === 'bank_transfer' ? 'EFT / Havale' : 'Kredi Kartı'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Ödeme Durumu</p>
                      {(() => {
                        const ps = showOrderDetails?.payment_status || showOrderDetails?.paymentStatus || 'pending';
                        if (ps === 'paid' || ps === 'completed') {
                          return <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 font-semibold">✅ Ödendi</span>;
                        }
                        if (ps === 'failed') {
                          return <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 font-semibold">❌ Başarısız</span>;
                        }
                        return <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800 font-semibold">⏳ Ödeme Bekleniyor</span>;
                      })()}
                    </div>
                  </div>

                  {/* Kargo ve Teslimat Bilgileri */}
                  {(() => {
                    const o = showOrderDetails || {};

                    // 1. Extract Name safely
                    let name = o?.fullName || o?.full_name || o?.profiles?.full_name;

                    // 2. Safe Address JSON Parser (handles stringified or native object)
                    let addr = {};
                    try {
                      let rawAddr = o?.shippingAddress || o?.shipping_address;
                      if (typeof rawAddr === 'string') {
                        addr = JSON.parse(rawAddr);
                        if (typeof addr === 'string') addr = JSON.parse(addr);
                      } else {
                        addr = rawAddr || {};
                      }
                    } catch(e) {
                      addr = {};
                    }

                    // 3. Name fallback & formatting
                    if (!name) {
                      name = addr?.ContactName || addr?.fullName || addr?.adSoyad || addr?.name || (o?.guestEmail || o?.email || "")?.split('@')?.[0];
                    }
                    if (name && typeof name === 'string') {
                      name = name?.trim()?.replace(/(^\w|\s\w)/g, m => m?.toUpperCase());
                    } else {
                      name = "Belirtilmedi";
                    }

                    // 4. Extract explicit cargo details based on database payload
                    const tcNo = addr?.tc || addr?.tc_kimlik || o?.tax_id || "Belirtilmedi";
                    const phoneNo = addr?.phone || addr?.Phone || addr?.telefon || o?.phone || "Belirtilmedi";
                    const emailAddr = o?.guestEmail || o?.userEmail || o?.email || addr?.Email || addr?.email || "Belirtilmedi";

                    const street = addr?.address || addr?.Address || addr?.fullAddress || addr?.acikAdres || "";
                    const district = addr?.district || addr?.District || addr?.ilce || "";
                    const city = addr?.city || addr?.City || addr?.il || "";
                    const zip = addr?.postal_code || addr?.postalCode || addr?.ZipCode || "";

                    return (
                      <div className="mt-4 mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                          📦 Kargo ve Teslimat Bilgileri
                        </h3>
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 shadow-sm">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Alıcı Adı Soyadı</p>
                              <p className="font-bold text-gray-900 text-base mt-0.5">{name}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">T.C. Kimlik / Vergi No</p>
                              <p className="font-mono font-semibold text-gray-800 mt-0.5">{tcNo}</p>
                            </div>

                            <div className="md:col-span-2 border-t border-gray-200 my-1"></div>

                            <div>
                              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Telefon Numarası</p>
                              <p className="text-gray-800 font-semibold mt-0.5">{phoneNo}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">E-posta Adresi</p>
                              <p className="text-gray-800 font-medium mt-0.5">{emailAddr}</p>
                            </div>

                            <div className="md:col-span-2 border-t border-gray-200 my-1"></div>

                            <div className="md:col-span-2">
                              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Açık Adres (Kargo Gönderim)</p>
                              <p className="text-gray-900 font-medium mt-1 leading-relaxed bg-white p-3 rounded-lg border border-gray-100 shadow-inner">
                                {street} {district && `${district} / `}{city} {zip && `(${zip})`}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Order Items */}
                  <div>
                    <h4 className="font-medium text-foreground mb-3">Sipariş İçeriği</h4>
                    <div className="space-y-3">
                      {showOrderDetails?.items?.map(item => (
                        <div key={item?.id} className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                          {item?.product?.image ? (
                            <img
                              src={item?.product?.image}
                              alt={item?.product?.title}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-background flex items-center justify-center">
                              <Icon name="Package" size={24} className="text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-foreground">{item?.product?.title}</p>
                            {(item?.selectedColor || item?.selected_color || item?.selectedSize || item?.selected_size) && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {(item?.selectedColor || item?.selected_color) && `Renk: ${item?.selectedColor || item?.selected_color}`}
                                {(item?.selectedColor || item?.selected_color) && (item?.selectedSize || item?.selected_size) && ' | '}
                                {(item?.selectedSize || item?.selected_size) && `Beden: ${item?.selectedSize || item?.selected_size}`}
                              </p>
                            )}
                            <p className="text-sm text-muted-foreground">
                              {item?.quantity} adet × {formatPrice(item?.priceAtPurchase)}
                            </p>
                          </div>
                          <p className="font-medium text-foreground">
                            {formatPrice(item?.quantity * item?.priceAtPurchase)}
                          </p>
                          <button
                            type="button"
                            onClick={() => handleDeleteItem(item?.id, item?.product?.title || item?.title || 'Ürün')}
                            disabled={removingItemId === item?.id}
                            className="text-red-600 hover:text-red-800 bg-red-50 border border-red-200 text-xs px-2 py-1 rounded font-medium ml-auto z-50 flex-shrink-0 disabled:opacity-40"
                          >
                            {removingItemId === item?.id ? '...' : '🗑️ Bu Kalemi Kaldır'}
                          </button>
                        </div>
                      ))}
                      {(() => {
                        const cartSubtotal = (showOrderDetails?.items || [])?.reduce((sum, item) => sum + ((item?.priceAtPurchase || 0) * (item?.quantity || 0)), 0);
                        const shippingFee = showOrderDetails?.shipping_fee || Math.max(0, (showOrderDetails?.totalAmount || 0) - cartSubtotal);
                        if (shippingFee > 0.1) {
                          return (
                            <div className="flex justify-between items-center py-3 border-t border-gray-100 bg-blue-50/50 px-4 mt-2 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-md flex items-center justify-center text-lg">📦</div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">Kargo ve Taşıma Bedeli</p>
                                  <p className="text-xs text-gray-500">Standart Gönderim</p>
                                </div>
                              </div>
                              <span className="font-bold text-gray-900">
                                {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' })?.format(shippingFee)}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  {/* Delivery Confirmation */}
                  {(showOrderDetails?.status === 'shipped' || showOrderDetails?.status === 'delivered') && (
                    <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Icon name="CheckCircle" size={16} className="text-green-600" />
                        <h5 className="font-semibold text-green-900 text-sm">Teslimat Onayı</h5>
                      </div>
                      {showOrderDetails?.status === 'delivered' ? (
                        <div className="flex items-center gap-2 p-3 bg-green-100 rounded-lg">
                          <Icon name="CheckCircle" size={18} className="text-green-600" />
                          <p className="text-sm font-semibold text-green-800">Sipariş teslim edildi olarak işaretlendi.</p>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-green-700 mb-3">
                            Müşteri siparişi teslim aldıysa aşağıdaki butona tıklayarak teslim edildi olarak işaretleyin. Müşteri panelinde sipariş durumu <strong>"Teslim Edildi"</strong> olarak güncellenecektir.
                          </p>
                          <button
                            className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                            onClick={async () => {
                              if (!window.confirm('Bu siparişi "Teslim Edildi" olarak işaretlemek istediğinize emin misiniz?')) return;
                              const { error: deliverError } = await supabase?.from('orders')?.update({ status: 'delivered' })?.eq('id', showOrderDetails?.id);
                              if (deliverError) { alert('Güncelleme sırasında hata oluştu: ' + deliverError?.message); return; }
                              setShowOrderDetails({ ...showOrderDetails, status: 'delivered' });
                              loadData();
                              alert('Sipariş "Teslim Edildi" olarak güncellendi. Müşteri panelinde durum yansıtıldı.');
                            }}
                          >
                            <Icon name="CheckCircle" size={16} />
                            Teslim Edildi Olarak İşaretle
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {/* Cancel Form */}
                  <div className="border-t border-border pt-5">
                    <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="Settings" size={18} className="text-primary" />
                      İşlem Merkezi
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                      {/* Shipping Form */}
                      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Icon name="Truck" size={16} className="text-blue-600" />
                          <h5 className="font-semibold text-blue-900 text-sm">Kargo Bilgileri</h5>
                        </div>
                        <div className="space-y-2 mb-3">
                          <select
                            className="w-full p-2.5 text-sm border border-blue-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none text-gray-700"
                            value={trackingData?.company || showOrderDetails?.tracking_company || ''}
                            onChange={(e) => setTrackingData({ ...trackingData, company: e?.target?.value })}
                          >
                            <option value="">Firma Seçin</option>
                            <option value="Yurtiçi Kargo">Yurtiçi Kargo</option>
                            <option value="MNG Kargo">MNG Kargo</option>
                            <option value="Aras Kargo">Aras Kargo</option>
                            <option value="Sürat Kargo">Sürat Kargo</option>
                            <option value="PTT Kargo">PTT Kargo</option>
                            <option value="UPS Türkiye">UPS Türkiye</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Takip Numarası"
                            className="w-full p-2.5 text-sm border border-blue-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none text-gray-700"
                            value={trackingData?.number || showOrderDetails?.tracking_number || ''}
                            onChange={(e) => setTrackingData({ ...trackingData, number: e?.target?.value })}
                          />
                        </div>
                        <button
                          className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                          onClick={async () => {
                            const effectiveCompany = trackingData?.company || showOrderDetails?.tracking_company || '';
                            const effectiveNumber = trackingData?.number || showOrderDetails?.tracking_number || '';
                            if (!effectiveCompany || !effectiveNumber) return alert('Lütfen kargo firması ve takip numarasını eksiksiz girin.');
                            const { error: updateError } = await supabase?.from('orders')?.update({ status: 'shipped', tracking_company: effectiveCompany, tracking_number: effectiveNumber })?.eq('id', showOrderDetails?.id);
                            if (updateError) { alert('Kayıt sırasında hata oluştu: ' + updateError?.message); return; }
                            setShowOrderDetails({ ...showOrderDetails, status: 'shipped', tracking_company: effectiveCompany, tracking_number: effectiveNumber });
                            loadData();

                            // Send shipping notification email to customer
                            let customerEmail = showOrderDetails?.userEmail || showOrderDetails?.guestEmail || showOrderDetails?.shippingAddress?.Email || showOrderDetails?.shippingAddress?.email || null;
                            if (customerEmail) {
                              try {
                                const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
                                const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
                                await fetch(`${supabaseUrl}/functions/v1/send-shipping-email`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${supabaseAnonKey}`,
                                  },
                                  body: JSON.stringify({
                                    orderId: showOrderDetails?.id,
                                    customerEmail,
                                    customerName: showOrderDetails?.shippingAddress?.fullName || showOrderDetails?.shippingAddress?.name || null,
                                    trackingCompany: effectiveCompany,
                                    trackingNumber: effectiveNumber,
                                  }),
                                });
                              } catch (emailErr) {
                                console.error('Kargo e-postası gönderilemedi:', emailErr);
                              }
                            }

                            alert('Kargo bilgileri başarıyla kaydedildi ve müşteri paneline yansıtıldı.' + (customerEmail ? ' Müşteriye bilgilendirme e-postası gönderildi.' : ''));
                          }}
                        >
                          Kargoya Verildi Kaydet
                        </button>
                      </div>

                      {/* Delivery Confirmation */}
                      <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Icon name="CheckCircle" size={16} className="text-green-600" />
                          <h5 className="font-semibold text-green-900 text-sm">Teslimat Onayı</h5>
                        </div>
                        {showOrderDetails?.status === 'delivered' ? (
                          <div className="flex items-center gap-2 p-3 bg-green-100 rounded-lg">
                            <Icon name="CheckCircle" size={18} className="text-green-600" />
                            <p className="text-sm font-semibold text-green-800">Sipariş teslim edildi olarak işaretlendi.</p>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm text-green-700 mb-3">
                              Müşteri siparişi teslim aldıysa aşağıdaki butona tıklayarak teslim edildi olarak işaretleyin. Müşteri panelinde sipariş durumu <strong>"Teslim Edildi"</strong> olarak güncellenecektir.
                            </p>
                            <button
                              className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                              onClick={async () => {
                                if (!window.confirm('Bu siparişi "Teslim Edildi" olarak işaretlemek istediğinize emin misiniz?')) return;
                                const { error: deliverError } = await supabase?.from('orders')?.update({ status: 'delivered' })?.eq('id', showOrderDetails?.id);
                                if (deliverError) { alert('Güncelleme sırasında hata oluştu: ' + deliverError?.message); return; }
                                setShowOrderDetails({ ...showOrderDetails, status: 'delivered' });
                                loadData();
                                alert('Sipariş "Teslim Edildi" olarak güncellendi. Müşteri panelinde durum yansıtıldı.');
                              }}
                            >
                              <Icon name="CheckCircle" size={16} />
                              Teslim Edildi Olarak İşaretle
                            </button>
                          </>
                        )}
                      </div>

                      {/* Manuel Ödeme Onayı */}
                      {(showOrderDetails?.payment_status !== 'paid' && showOrderDetails?.payment_status !== 'completed') && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div>
                            <h5 className="font-bold text-amber-800 text-sm">Manuel Ödeme Onayı</h5>
                            <p className="text-xs text-amber-700">EFT/Havale ödemesi hesaba geçtiyse buradan onaylayın.</p>
                          </div>
                          <button
                            onClick={async () => {
                              if (!window.confirm('Bu siparişi ödendi olarak işaretlemek istediğinize emin misiniz?')) return;
                              const { error: payErr } = await supabase?.from('orders')?.update({ payment_status: 'paid' })?.eq('id', showOrderDetails?.id);
                              if (payErr) { alert('Güncelleme hatası: ' + payErr?.message); return; }
                              setShowOrderDetails({ ...showOrderDetails, payment_status: 'paid' });
                              loadData();
                              alert('Ödeme durumu "Ödendi" olarak güncellendi.');
                            }}
                            className="whitespace-nowrap bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-6 rounded-lg shadow transition-colors"
                          >
                            ✓ Ödendi İşaretle
                          </button>
                        </div>
                      )}

                      {/* Cancel Form */}
                      <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Icon name="XCircle" size={16} className="text-red-600" />
                          <h5 className="font-semibold text-red-900 text-sm">Siparişi İptal Et</h5>
                        </div>
                        <textarea
                          className="w-full p-2.5 text-sm border border-red-200 rounded-lg bg-white focus:ring-2 focus:ring-red-500 outline-none mb-3 resize-none text-gray-700"
                          rows="3"
                          placeholder="Müşteriye e-posta ile iletilecek iptal nedenini buraya yazın..."
                          value={cancelReason || showOrderDetails?.cancel_reason || ''}
                          onChange={(e) => setCancelReason(e?.target?.value)}
                        />
                        <button
                          className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                          onClick={async () => {
                            const effectiveCancelReason = cancelReason || '';
                            if (!effectiveCancelReason?.trim()) return alert('Lütfen müşteriye iletilmek üzere bir iptal nedeni girin.');
                            if (!window.confirm('Bu siparişi iptal edip müşteriye anında e-posta göndermek istediğinize emin misiniz?')) return;
                            const { error: cancelError } = await supabase?.from('orders')?.update({ status: 'cancelled', cancel_reason: effectiveCancelReason })?.eq('id', showOrderDetails?.id);
                            if (cancelError) { alert('Kayıt sırasında hata oluştu: ' + cancelError?.message); return; }
                            // Extract customer email from all possible sources
                            let customerEmail = showOrderDetails?.guestEmail || showOrderDetails?.guest_email || showOrderDetails?.userEmail || null;
                            if (!customerEmail && showOrderDetails?.shippingAddress) {
                              try {
                                let addrObj = typeof showOrderDetails?.shippingAddress === 'string' ? JSON.parse(showOrderDetails?.shippingAddress) : showOrderDetails?.shippingAddress;
                                customerEmail = addrObj?.Email || addrObj?.email || null;
                              } catch(e) {}
                            }
                            if (!customerEmail && showOrderDetails?.shipping_address) {
                              try {
                                let addrObj = typeof showOrderDetails?.shipping_address === 'string' ? JSON.parse(showOrderDetails?.shipping_address) : showOrderDetails?.shipping_address;
                                customerEmail = addrObj?.Email || addrObj?.email || null;
                              } catch(e) {}
                            }
                            if (customerEmail) {
                              await supabase?.functions?.invoke('send-order-cancellation-email', {
                                body: { orderId: showOrderDetails?.id, cancelReason: effectiveCancelReason, customerEmail: customerEmail }
                              });
                            }
                            setShowOrderDetails({ ...showOrderDetails, status: 'cancelled', cancel_reason: effectiveCancelReason });
                            setCancelReason('');
                            loadData();
                            alert('Sipariş iptal edildi' + (customerEmail ? ' ve müşteriye e-posta gönderildi.' : '. (Müşteri e-postası bulunamadı, e-posta gönderilemedi.)'));
                          }}
                        >
                          İptal Et ve E-posta Gönder
                        </button>
                      </div>

                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Order Confirmation Modal */}
        {deleteOrderConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl border border-border w-full max-w-md shadow-xl">
              <div className="flex items-center gap-3 p-5 border-b border-border">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="Trash2" size={20} className="text-red-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-foreground">Siparişi Sil</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">#{deleteOrderConfirm?.id?.slice(0, 8)}</p>
                </div>
              </div>
              <div className="p-5">
                <p className="text-sm text-foreground mb-6">
                  Bu kaydı kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteOrderConfirm(null)}
                    disabled={deleteOrderLoading}
                    className="flex-1 px-4 py-2 rounded-lg border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleDeleteOrder}
                    disabled={deleteOrderLoading}
                    className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  >
                    {deleteOrderLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Icon name="Trash2" size={14} />
                    )}
                    Evet, Sil
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast && (
          <div className={`fixed bottom-6 right-6 z-[60] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all ${
            toast?.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
          }`}>
            <Icon name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={16} />
            {toast?.message}
          </div>
        )}

        <SmartAddProductModal
          isOpen={showSmartAddModal}
          onClose={() => setShowSmartAddModal(false)}
          onSuccess={() => {
            loadData();
          }}
        />
      </div>
    </div>
  );
};

export default StoreManager;